import { Phone, ClipboardCheck, CheckCircle2, FileText, CalendarClock } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, stagger, item } from "../lib/motion";

const STEPS = [
  {
    icon: Phone,
    title: "Contact Recovery Rides",
    body: "Call, text, or message us through WhatsApp with the transportation or concierge service you need.",
  },
  {
    icon: ClipboardCheck,
    title: "Confirm the Details",
    body: "We will confirm availability, pickup information, destination, timing, estimated rate, and payment arrangements.",
  },
  {
    icon: CheckCircle2,
    title: "Complete the Service",
    body: "A payment method must be saved and will be automatically charged after the ride or concierge service is completed. The passenger, family, treatment provider, or another authorized party may pay.",
  },
];

export const HowItWorks = () => (
  <section id="how" className="relative bg-[#FAFAF9] py-20 sm:py-28 overflow-hidden">
    <div className="pointer-events-none absolute top-1/2 right-0 h-96 w-96 -translate-y-1/2 rounded-full bg-[#1AD1B9]/10 blur-3xl" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
      <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-14 items-center">
        <Reveal delay={0.05} className="order-2 lg:order-1">
          <div className="relative rounded-[2rem] rounded-tl-[3.5rem] overflow-hidden shadow-xl shadow-[#3B3A4E]/12 aspect-[3/2] bg-[#F5F4F1]">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/dc8a482d-361d-4996-9eb5-a4cbac9ec941/images/75e60f81637bd51c0d8fafa454cffe7d5a1738369aa0cfc5d38aba7a2f5ee9f6.jpeg"
              alt="Illustration of Michael showing how simple it is to request a ride: contact, confirm, and complete the service"
              className="h-full w-full object-cover object-center"
            />
          </div>
        </Reveal>
        <Reveal className="max-w-xl order-1 lg:order-2">
          <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-[#0891B2] mb-4">
            How It Works
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#3B3A4E] leading-tight">
            Requesting Service Is Simple
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#5C5975] leading-relaxed">
            No app, no complicated forms—just a quick call, text, or message. Michael and the Recovery Rides
            team handle the rest, from confirming details to getting your passenger there safely.
          </p>
        </Reveal>
      </div>

      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        className="mt-12 grid md:grid-cols-3 gap-6 lg:gap-8"
      >
        {STEPS.map((s, i) => (
          <motion.div key={s.title} variants={item} className="relative" data-testid={`step-card-${i}`}>
            <div className="h-full rounded-3xl bg-white border border-[#3B3A4E]/8 p-8 shadow-sm transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
              <div className="flex items-center justify-between mb-6">
                <div className="h-14 w-14 rounded-2xl brand-gradient flex items-center justify-center shadow-md shadow-teal-500/20">
                  <s.icon size={26} className="text-white" />
                </div>
                <span className="font-mono text-5xl font-bold text-[#3B3A4E]/8">{i + 1}</span>
              </div>
              <h3 className="text-lg sm:text-xl font-heading font-semibold text-[#2D2B3D]">{s.title}</h3>
              <p className="mt-3 text-sm sm:text-base text-[#5C5975] leading-relaxed">{s.body}</p>
            </div>
          </motion.div>
        ))}
      </motion.div>

      <div className="mt-8 grid md:grid-cols-2 gap-6">
        <Reveal>
          <div className="flex items-start gap-4 rounded-2xl bg-white border border-[#3B3A4E]/8 p-6">
            <FileText size={22} className="text-[#0891B2] shrink-0 mt-0.5" />
            <p className="text-sm sm:text-base text-[#5C5975] leading-relaxed">
              <span className="font-semibold text-[#2D2B3D]">Provider billing:</span> Treatment and recovery
              providers that consistently use Recovery Rides may contact us to discuss invoicing arrangements.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="flex items-start gap-4 rounded-2xl bg-white border border-[#3B3A4E]/8 p-6">
            <CalendarClock size={22} className="text-[#0891B2] shrink-0 mt-0.5" />
            <p className="text-sm sm:text-base text-[#5C5975] leading-relaxed">
              <span className="font-semibold text-[#2D2B3D]">Availability:</span> Services may be requested
              immediately or scheduled in advance. Recovery Rides operates 24/7 based on driver availability.
              Advance scheduling is encouraged whenever possible.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
